import React, { useEffect, useState } from 'react';
import { Switch } from '../../../../../components/ui/switch';
import { getAgentConfig, saveAgentConfig } from '../../../services/agentConfigApi';
import { toast } from '@/hooks/use-toast';

interface AgentPanelHeaderProps {
    agentId?: string;
    agentName?: string;
    middle?: React.ReactNode;
    onClose?: () => void;
}

const AgentPanelHeader: React.FC<AgentPanelHeaderProps> = ({ agentId, agentName, middle }) => {
    const [iaActiva, setIaActiva] = useState(true);
    const [loading, setLoading] = useState(false);
    const [initialLoaded, setInitialLoaded] = useState(false);

    // Load initial ia_activa state from database
    useEffect(() => {
        if (!agentId) return;
        
        const loadConfig = async () => {
            try {
                const config = await getAgentConfig(agentId);
                if (config) {
                    setIaActiva((config as any).ia_activa ?? true);
                }
                setInitialLoaded(true);
            } catch (error) {
                console.error('Error loading ia_activa:', error);
                setInitialLoaded(true);
            }
        };
        
        loadConfig();
    }, [agentId]);

    const handleToggleIA = async (checked: boolean) => {
        if (!agentId || loading) return;

        setLoading(true);
        const previousValue = iaActiva;
        setIaActiva(checked); // Optimistic update

        try {
            await saveAgentConfig({
                agent_id: agentId,
                ia_activa: checked,
            } as any);
            toast.success(checked ? 'IA activada' : 'IA desactivada');
        } catch (error) {
            console.error('Error updating ia_activa:', error);
            setIaActiva(previousValue); // Rollback
            toast.error('No se pudo actualizar el estado de la IA');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full bg-white flex items-center px-6 py-4 rounded-t-[16px]">
            <div className="flex-1 flex items-center gap-4">
                {/* IA Active toggle */}
                {initialLoaded && (
                    <div className="flex items-center gap-3">
                        <Switch
                            checked={iaActiva}
                            onCheckedChange={handleToggleIA}
                            disabled={loading || !agentId}
                        />
                        <span className={`${iaActiva ? 'text-green-600' : 'text-gray-500'} text-sm font-medium`}>
                            {iaActiva ? 'IA Activa' : 'IA Desactivada'}
                        </span>
                    </div>
                )}

                {agentName && (
                    <h1 className="text-foreground font-montserrat text-xl font-bold tracking-wide">
                        {agentName}
                    </h1>
                )}
            </div>

            <div className="flex-1 flex items-center">
                {middle}
            </div>

            <div className="flex-1 flex items-center justify-end">
                {/* Removed save button - saving happens automatically when toggle changes */}
            </div>
        </div>
    );
};

export default AgentPanelHeader;
